import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { AgreementReadModel } from '@client/api';
import {
  isDefinedString,
  TableCell,
  TableRow,
  Text,
  toIntervalEndDateString,
  toIntervalStartDateString,
  toMoneyStringOrUndefined,
} from '@client/toolkit';
import { colors } from '../../../../../styles/color-tokens';
import { useAgreementPartiesNumerationText } from '../../../../agreements/hooks/use-agreement-parties-numeration-text';

interface CompanyRentalAgreementRowProps {
  agreement?: AgreementReadModel;
  isFetching?: boolean;
}

export const CompanyRentalAgreementRow = ({ agreement, isFetching }: CompanyRentalAgreementRowProps) => {
  const navigate = useNavigate();
  const { t: tCommon } = useTranslation();
  const { t: tType } = useTranslation('type');

  const owners = useAgreementPartiesNumerationText(agreement, 'Owner');
  const renters = useAgreementPartiesNumerationText(agreement, 'Tenant');

  const handleClick = () => {
    if (agreement?.id) {
      navigate(`/agreements/${agreement.id}`);
    }
  };

  return (
    <TableRow onClick={handleClick} className="cursor-pointer hover:bg-surface-secondary">
      {/* Leietaker */}
      <TableCell>
        <Text
          isEmpty={!isDefinedString(renters)}
          loading={isFetching}
          emptyValue={<span className="italic">{tCommon('none')}</span>}
        >
          {renters}
        </Text>
      </TableCell>
      {/* Eier */}
      <TableCell>
        <Text
          isEmpty={!isDefinedString(owners)}
          loading={isFetching}
          emptyValue={<span className="italic">{tCommon('none')}</span>}
        >
          {owners}
        </Text>
      </TableCell>
      {/* Avtalenavn */}
      <TableCell>
        <Text className="text-brand-primary" loading={isFetching} isEmpty={!isDefinedString(agreement?.name)}>
          {agreement?.name}
        </Text>
      </TableCell>
      {/* Utleiestart */}
      <TableCell>
        <Text isEmpty={!isDefinedString(toIntervalStartDateString(agreement?.duration))} loading={isFetching}>
          {toIntervalStartDateString(agreement?.duration)}
        </Text>
      </TableCell>
      {/* Utleieslutt */}
      <TableCell>
        <Text isEmpty={!isDefinedString(toIntervalEndDateString(agreement?.duration))} loading={isFetching}>
          {toIntervalEndDateString(agreement?.duration)}
        </Text>
      </TableCell>
      {/* Årlig leiebeløp */}
      <TableCell>
        <Text isEmpty={!isDefinedString(toMoneyStringOrUndefined(agreement?.totalYearlyAmountProRated))} loading={isFetching}>
          {toMoneyStringOrUndefined(agreement?.totalYearlyAmountProRated)}
        </Text>
      </TableCell>
      {/* Status */}
      <TableCell>
        <Text
          isEmpty={!isDefinedString(agreement?.status)}
          loading={isFetching}
          className={agreement?.status === 'Effective' ? `text-[${colors.status.success}]` : undefined}
        >
          {tType(`AgreementStatus.${agreement?.status}`)}
        </Text>
      </TableCell>
    </TableRow>
  );
};
